import React from 'react'
import * as graph from '@adc/sdk'
import type {
  AuctionTransaction,
  AuctionTransactionParty,
  Listing,
  Maybe,
} from '@adc/sdk/types'
import { PropertyInfo } from '../../../listing/popupCard/cardParts'
import Icon from '../../../elementals/icon'
import InfoPill from '../../../elementals/infoPill'
import { ColorNames } from '../../../../constants/colorVars'
import { formatListingImgUrl } from '../../../listing/utils/formatters'
import { getListingImageUrl } from '../../../../utils/listing'
import classnames from 'classnames/bind'
import cls from './styles.scss'

// @ts-ignore unsafe assignment of any
const cx = classnames.bind(cls)

const CARD_PHOTO_WIDTH = 110

const getPhotoUrl = (primaryPhoto: string, multiplier = 1) =>
  formatListingImgUrl(primaryPhoto, {
    width: CARD_PHOTO_WIDTH * multiplier,
  }) as string

/**
 * Extract the ID portion from a URN like "adc:transaction:abc123"
 */
const extractUrnId = (urn: string): string => {
  if (!urn) return ''
  const parts = urn.split(':')
  return parts.length >= 3 ? parts.slice(2).join(':') : urn
}

/**
 * Determine if the transaction is a lead (no listing associated)
 */
const isLead = (transaction: Partial<AuctionTransaction>): boolean => {
  return !transaction.listing?.listing_id
}

const LEAD_PREFERRED_ROLES = [
  graph.enums.ListingPartyRole.AssetManager,
  graph.enums.ListingPartyRole.PrivateSeller,
  graph.enums.ListingPartyRole.SellerAssetManager,
  graph.enums.ListingPartyRole.Servicer,
]

/**
 * Find the primary contact party for a lead transaction
 */
const getLeadParty = (
  parties?: Maybe<Array<Maybe<AuctionTransactionParty>>>
): Maybe<AuctionTransactionParty> => {
  if (!parties?.length) return null
  const preferred = parties.find(
    p =>
      p?.party_role &&
      LEAD_PREFERRED_ROLES.includes(
        p.party_role as graph.enums.ListingPartyRole
      )
  )
  return preferred || parties[0]
}

interface IAddressProps {
  address: Maybe<string[]>
  elmId: string
}

const Address = ({ address, elmId }: IAddressProps) => (
  <div className={cx('address-container', 'nowrap')}>
    <div
      data-elm-id={`${elmId}_address_field_1`}
      className={cx('address-field', 'address-field-1', 'nowrap')}
    >
      {address?.[0]}
    </div>
    <div
      data-elm-id={`${elmId}_address_field_2`}
      className={cx('address-field', 'nowrap')}
    >
      {address?.[1]}
    </div>
  </div>
)

/**
 * Build an address array from property summary data
 */
const buildAddressFromProperty = (
  transaction: Partial<AuctionTransaction>
): string[] => {
  const summary = transaction.property?.summary
  if (!summary?.address) return ['No address available']
  const {
    street_number,
    street_name,
    municipality,
    country_primary_subdivision,
    postal_code,
  } = summary.address as Record<string, string | undefined>
  const line1 =
    [street_number, street_name].filter(Boolean).join(' ') || 'Unknown address'
  const parts = [municipality, country_primary_subdivision, postal_code].filter(
    Boolean
  )
  const line2 = parts.join(', ')
  return [line1, line2 || '']
}

export interface ITransactionItemProps {
  transaction: Maybe<Partial<AuctionTransaction>>
  elmId: string
  className?: string
  onClose?: () => void
  viewMode?: boolean
}

const TransactionItem = ({
  transaction,
  elmId,
  className,
  onClose,
  viewMode = false,
}: ITransactionItemProps) => {
  if (!transaction) return null

  const transactionIsLead = isLead(transaction)
  const urnId = extractUrnId(transaction.transaction_urn || '')
  const leadParty = transactionIsLead ? getLeadParty(transaction.parties) : null

  // Determine address to display
  const listingAddress = transaction.listing?.formatted_address as
    | string[]
    | undefined
  const hasListingAddress =
    listingAddress && listingAddress.length > 0 && listingAddress[0]
  const address: string[] = hasListingAddress
    ? listingAddress
    : buildAddressFromProperty(transaction)

  // Property info (bed/bath/sqft) from transaction-level property
  const propertySummary = transaction.property?.summary

  // Determine photo
  const listingPhoto = transaction.listing?.primary_photo
  const photoUrl = listingPhoto ? getPhotoUrl(listingPhoto, 2) : null
  const defaultPhotoUrl = getListingImageUrl() as string

  return (
    <div data-elm-id={elmId} className={cx('transaction-item', className)}>
      {onClose && !viewMode && (
        <Icon
          className={cx('close-icon')}
          name='xmark'
          iconStyle='regular'
          onClick={onClose}
          size='medium'
        />
      )}
      <div className={cx('photo-section')}>
        <img
          src={photoUrl || defaultPhotoUrl}
          alt='Transaction property'
          className={cx('photo')}
        />
        {transaction.listing?.is_searchable &&
          transaction.listing?.is_published && (
            <Icon
              className={cx('published-icon')}
              name='globe'
              iconStyle='solid'
              size='large'
              color='adcLightGreen'
            />
          )}
        {transactionIsLead && (
          <InfoPill
            elmId={`${elmId}_lead_badge`}
            content='Lead'
            size='x-small'
            backgroundColor={ColorNames.Caution}
            foregroundColor={ColorNames.White}
            className={cx('lead-badge')}
          />
        )}
      </div>
      <div className={cx('info-section')}>
        <Address elmId={elmId} address={address} />

        {propertySummary && (
          <PropertyInfo
            listing={
              {
                primary_property: {
                  summary: propertySummary,
                },
              } as Listing
            }
            className={cx('property-info')}
          />
        )}

        {leadParty?.party_name && (
          <div className={cx('party-info')}>
            <span className={cx('party-role')}>
              {leadParty.party_role?.replace(/_/g, ' ')}:
            </span>{' '}
            {leadParty.party_name}
          </div>
        )}

        <div className={cx('id-section')}>
          <span className={cx('urn-id')}>
            TX: <b>{urnId}</b>
          </span>
          {transaction.listing?.listing_id && (
            <span className={cx('listing-id')}>
              Listing: <b>{transaction.listing.listing_id}</b>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default TransactionItem
